import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/format';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  children: ReactNode;
  footer?: ReactNode;
  closeOnBackdrop?: boolean;
}

const sizeClass = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  '2xl': 'max-w-5xl',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
  closeOnBackdrop = true,
}: ModalProps) {
  // Lock body scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        className={cn(
          // Constrain modal height to viewport so long content scrolls internally
          // instead of overflowing behind the browser chrome. Header + footer stay
          // pinned; the body is the only scrollable region.
          'relative z-10 w-full bg-surface border border-line rounded-lg shadow-hover',
          'flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)]',
          sizeClass[size],
        )}
      >
        {(title || description) && (
          <div className="px-6 pt-6 pb-4 border-b border-line shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                {title && (
                  <h2 className="text-subhead font-display font-semibold text-ink">
                    {title}
                  </h2>
                )}
                {description && (
                  <p className="text-small text-ink-subtle mt-1">{description}</p>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-neutral hover:text-ink transition-colors p-1 -m-1 shrink-0"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        )}
        <div className="px-6 py-5 flex-1 overflow-y-auto overscroll-contain">
          {children}
        </div>
        {footer && (
          <div className="px-6 py-4 border-t border-line bg-canvas/50 flex justify-end gap-2 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
